import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

import { USER_MANAGEMENT_URL } from '../constants/route-paths';
import { SUPERADMIN_MODULE_FORBIDDEN_CODE } from '../utils/conversion-error-codes.util';
import { HttpStatus } from '../utils/http-status.util';

/**
 * True while a redirect to User Management is in flight. A page often fires
 * several requests at once, so a burst of refusals would otherwise start the
 * same navigation repeatedly (`router.url` only changes once it completes).
 * Cleared in `.finally` (after a `.catch`), so it can never stay stuck after a failed navigation.
 */
let navigatingToUserManagement = false;

/**
 * Safety net for a SuperAdmin who reaches a screen they may not use: the
 * backend answers with 403 `SUPERADMIN_MODULE_FORBIDDEN`, and we move them to
 * User Management instead of leaving an error on a page that can never work.
 *
 * Keyed on the `errorCode`, not the status, so an ordinary 403 ("wrong role"
 * elsewhere) is untouched, and it never reaches the forced-logout path: a 403
 * is not "session gone". The error is re-thrown so the calling page's own
 * handler still runs (and can suppress its toast).
 */
export const superAdminModuleForbiddenInterceptor: HttpInterceptorFn = (request, next) => {
  const router = inject(Router);

  return next(request).pipe(
    catchError((error: unknown) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === HttpStatus.Forbidden &&
        error.error?.errorCode === SUPERADMIN_MODULE_FORBIDDEN_CODE &&
        !router.url.startsWith(USER_MANAGEMENT_URL) &&
        !navigatingToUserManagement
      ) {
        navigatingToUserManagement = true;
        // `.catch` first: a rejected navigation must not surface as an unhandled
        // rejection, and `.finally` still clears the flag afterwards.
        void router
          .navigateByUrl(USER_MANAGEMENT_URL)
          .catch(() => false)
          .finally(() => {
            navigatingToUserManagement = false;
          });
      }
      return throwError(() => error);
    }),
  );
};
